import { type TypedDocumentNode, gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { CircularProgress } from '@sorare/core/src/atoms/loader/CircularProgress';
import { BrandXS } from '@sorare/core/src/atoms/typography';
import { proxyUrl } from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { isType } from '@sorare/core/src/lib/gql';

import type { MoveToStarPackButton_cardPack } from './__generated__/index.graphql';

const Root = styled.div`
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
`;
const StyledVertical = styled(Vertical)`
  align-items: flex-start;
`;
const PlayerFaceWrapper = styled.div`
  width: 54px;
  padding: var(--half-unit);
  border-radius: var(--triple-unit) 0 0 var(--triple-unit);
  border-top: 1px solid var(--c-nd-150);
  border-left: 1px solid var(--c-nd-150);
  border-bottom: 1px solid var(--c-nd-150);
  background: var(--c-nd-50);
`;
const PlayerFace = styled.div`
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
  border-radius: 100%;
  background: var(--c-nd-700);
  overflow: hidden;
`;
const PlayerFaceImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
const ProgressWrapper = styled(Horizontal).attrs({ center: true })`
  position: relative;
  width: var(--quadruple-unit);
  height: var(--quadruple-unit);
`;
const StyledCircularProgress = styled(CircularProgress)`
  --circular-progress-stroke: var(--c-yellow-400);
  position: absolute;
  inset: 0;
`;

type Props = {
  onClick: () => void;
  cardPack: MoveToStarPackButton_cardPack;
};
export const MoveToStarPackButton = ({ onClick, cardPack }: Props) => {
  const { rewardedByTask } = cardPack;
  if (!rewardedByTask || !isType(rewardedByTask, 'StarPackTask')) {
    return null;
  }
  const { starPlayer, progress, target } = rewardedByTask;
  const { avatarPictureUrl, displayName } = starPlayer.anyPlayer;
  return (
    <Root>
      <ButtonBase onClick={onClick}>
        <StyledVertical>
          <PlayerFaceWrapper>
            <PlayerFace>
              <PlayerFaceImage
                src={proxyUrl(avatarPictureUrl, { cropWidth: 80 })}
                alt={displayName}
              />
            </PlayerFace>
          </PlayerFaceWrapper>
          <BrandXS
            className="text-gradient-color"
            style={
              {
                '--text-gradient-color': 'var(--c-gradient-gold)',
              } as CSSProperties
            }
          >
            <FormattedMessage
              id="BuyPacks.MoveToStarPackButton"
              defaultMessage="Star"
            />
          </BrandXS>
          <ProgressWrapper>
            <StyledCircularProgress
              progress={progress}
              maxProgress={target}
              strokeWidth={12}
            />
            <ChevronRightBold />
          </ProgressWrapper>
        </StyledVertical>
      </ButtonBase>
    </Root>
  );
};

MoveToStarPackButton.fragments = {
  cardPack: gql`
    fragment MoveToStarPackButton_cardPack on CardPack {
      slug
      rewardedByTask {
        id
        ... on StarPackTask {
          id
          progress
          target
          starPlayer {
            id
            anyPlayer {
              slug
              avatarPictureUrl
              displayName
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<MoveToStarPackButton_cardPack>,
};
